import { useState } from "react";

interface FAQItem {
  q: string;
  a: string;
}

interface FAQSectionProps {
  items?: FAQItem[];
  heading?: string;
}

const defaultFAQs: FAQItem[] = [
  {
    q: "Who provides landscaping services near me in London, Ontario?",
    a: "Upper Canada Landscape has been serving London, Ontario and surrounding communities for over 30 years. We provide full-service landscape design, construction, hardscaping, water features, and synthetic turf throughout Southwestern Ontario.",
  },
  {
    q: "How much does landscaping cost in London, Ontario?",
    a: "Landscaping costs vary depending on the scope of your project. Small projects like garden bed installations may start around $2,000, while comprehensive backyard transformations with patios, water features, and stonework can range from $15,000 to $80,000+. We provide free, transparent estimates for every project.",
  },
  {
    q: "How long does a landscaping project take to complete?",
    a: "Most residential landscaping projects take between 1 to 4 weeks depending on complexity. A simple patio installation may be completed in 3-5 days, while a full backyard redesign with water features and hardscape elements typically takes 2-4 weeks. We provide a clear timeline before starting any project.",
  },
  {
    q: "Do you install water features and ponds in London, Ontario?",
    a: "Yes. Water feature installation is one of our specialties. We design and build custom ponds, waterfalls, streams, and pondless water features for residential properties throughout London, Ontario and the surrounding area.",
  },
  {
    q: "What is synthetic turf and is it a good choice for my property?",
    a: "Synthetic turf is a premium artificial grass system that looks and feels like natural lawn without the maintenance. It\u2019s ideal for homeowners wanting year-round green lawn, pet-friendly surfaces, putting greens, or recreational spaces. Our synthetic turf products carry a 10-year warranty.",
  },
  {
    q: "Are you licensed and insured for landscaping in Ontario?",
    a: "Yes. Upper Canada Landscape is fully licensed and insured to operate in Ontario. We carry comprehensive liability insurance and our crews are trained professionals. You can hire us with complete confidence in our accountability and workmanship.",
  },
  {
    q: "Do you offer free estimates for landscaping projects?",
    a: "Absolutely. We offer free, no-obligation consultations and project estimates. Simply call us at 519-473-8111, email info@uppercanadalandscape.com, or submit a request through our contact form. We\u2019ll schedule a site visit at your convenience.",
  },
  {
    q: "What areas outside of London do you serve?",
    a: "We serve a broad area throughout Southwestern Ontario including St. Thomas, Sarnia, Kitchener, Stratford, Grand Bend, and communities throughout Middlesex, Elgin, Oxford, and Huron Counties. Contact us to confirm service availability in your area.",
  },
];

export default function FAQSection({
  items = defaultFAQs,
  heading = "Frequently Asked Questions",
}: FAQSectionProps) {
  const [open, setOpen] = useState<number | null>(null);

  const toggle = (i: number) => setOpen(open === i ? null : i);

  return (
    <section className="bg-stone-50 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16 items-start">
          {/* Left sticky heading */}
          <div className="lg:sticky lg:top-28">
            <span className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-rust-600 mb-4 block">
              Voice Search Ready
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-charcoal leading-tight mb-6">
              {heading}
            </h2>
            <p className="font-sans text-sm text-stone-500 leading-relaxed mb-8">
              Questions designed for how people actually search \u2014 by voice, mobile, and AI assistants.
            </p>
            <div className="bg-white border border-stone-200 rounded-xl p-5">
              <p className="font-sans text-xs text-stone-500 mb-3">Still have questions?</p>
              <a
                href="tel:5194738111"
                className="flex items-center gap-2 font-sans text-sm font-semibold text-rust-700 hover:text-rust-800 cursor-pointer"
              >
                <i className="ri-phone-fill"></i>
                519-473-8111
              </a>
            </div>
          </div>

          {/* Right accordion */}
          <div className="lg:col-span-2 space-y-1">
            {items.map((item, i) => (
              <div key={i} className="border border-stone-200 rounded-lg bg-white overflow-hidden">
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center justify-between text-left px-6 py-5 gap-4 hover:bg-stone-50 transition-colors cursor-pointer"
                  aria-expanded={open === i}
                >
                  <span className="font-sans text-sm md:text-base font-semibold text-charcoal leading-snug">
                    {item.q}
                  </span>
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-stone-100">
                    <i
                      className={`text-charcoal text-sm transition-transform duration-300 ${
                        open === i ? "ri-subtract-line rotate-0" : "ri-add-line"
                      }`}
                    ></i>
                  </span>
                </button>
                {open === i && (
                  <div className="px-6 pb-6 pt-0">
                    <p className="font-sans text-sm text-stone-500 leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
